import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface OnboardingState {
  step: string;
  version: number;
  workspaceName: string;
  isComplete: boolean;
}

/**
 * PersonalSetup page (R6) — the personal flow's first step.
 *
 * Personal onboarding is its own calm, personal-first flow — not a
 * stripped-down business one. This step asks only for the name of the
 * user's space. There is deliberately no website field, no team/company
 * language, and no business framing. Submitting advances to the outcomes
 * step.
 */
export function PersonalSetupPage() {
  const [workspaceName, setWorkspaceName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    async function loadState() {
      try {
        const response = await fetch('/api/onboarding/state', { credentials: 'include' });
        const data = await response.json();
        if (response.ok && data.state) {
          const state = data.state as OnboardingState;
          if (state.workspaceName) setWorkspaceName(state.workspaceName);
        }
      } catch {
        // Non-blocking — the user can still fill the form.
      } finally {
        setIsLoading(false);
      }
    }
    loadState();
  }, []);

  const handleContinue = async () => {
    if (!workspaceName.trim()) return;

    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: 'personal-setup',
          data: { workspaceName: workspaceName.trim() },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save your space');
        return;
      }

      navigate(onboardingPath('personal', 'outcomes', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="personal-setup" workspaceType="personal" onboardingMode="personal">
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </OnboardingLayout>
    );
  }

  const nextSteps = [
    "Choose what you'd like your space to help with",
    'See your plan',
    "That's it — your space is ready",
  ];

  return (
    <OnboardingLayout currentStep="personal-setup" workspaceType="personal" onboardingMode="personal" wide>
      <div className="grid overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 lg:grid-cols-5">
        <div className="p-4 sm:p-10 lg:col-span-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Personal space</p>
          <h1 className="mt-2 text-xl sm:text-3xl font-semibold tracking-tight text-gray-900">Set up your space</h1>
          <p className="mt-2 text-gray-500">
            A calm home for everything you want to stay on top of. Let's start with a name.
          </p>

          <div className="mt-8">
            <label htmlFor="space-name" className="block text-sm font-medium text-gray-700 mb-2">
              What should we call your space?
            </label>
            <input
              id="space-name"
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleContinue();
              }}
              placeholder="My Space"
              className="w-full px-4 py-3 bg-white text-gray-900 placeholder:text-gray-400 text-base border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">Just for you — you can rename it anytime.</p>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-end gap-3 border-t border-gray-100 pt-6">
            <button
              type="button"
              onClick={handleContinue}
              disabled={isSaving || !workspaceName.trim()}
              className="px-5 py-2.5 sm:px-7 sm:py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-600/25"
            >
              {isSaving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>
        <aside className="relative hidden lg:flex lg:col-span-2 flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-8 text-white">
          <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">What's next</p>
            <ol className="mt-6 space-y-5">
              {nextSteps.map((item, i) => (
                <li key={item} className="flex gap-3">
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-emerald-200 ring-1 ring-white/15">
                    {i + 1}
                  </span>
                  <span className="pt-0.5 text-sm leading-relaxed text-slate-200">{item}</span>
                </li>
              ))}
            </ol>
          </div>
          <p className="relative mt-8 text-xs text-slate-400">Takes about a minute. You can change everything later.</p>
        </aside>
      </div>
    </OnboardingLayout>
  );
}